import { EmptyFormikQuote, type QuoteFormikType } from '../formik/quote';
import type { Contact } from '../types/api/Contact/Contact';
import Enums from '../types/api/Enums';
import type { FixedPricingByCompany } from '../types/api/FixedPricingByCompany/FixedPricingByCompany';
import type { Project } from '../types/api/Project/Project';
import { QuoteConfig } from '../types/api/QuoteConfig/QuoteConfig';
import type { InputOption } from '../types/inputOption';
import type { List } from '../types/list';
import type { ProductSectionProps } from '../types/productSectionProps';
import {
  type TenantDefaultMargins,
  EmptyTenantDefaultMargins,
} from '../types/tenantDefaultMargins';
import { lookups } from '../utils/domainHelpers';
import { hasValue } from '../utils/equality';
import {
  applyTierPricingToProductsByPlants,
  projectProductsToProductsByPlants,
  quoteConfigProductsToProductsByPlants,
} from '../utils/quoteHelpers';
import type { YupSchemaNullableReferenceType } from '../utils/yupHelpers';

type UseQuoteDrawerReturn = {
  onCompanyChange: (
    companyID: string | null,
    contactList: List<Contact>,
  ) => YupSchemaNullableReferenceType[];

  onProjectChange: (project: Project | undefined) => {
    company: QuoteFormikType['company'];
    contacts: QuoteFormikType['contacts'];
  };

  handleProductState: (opts: {
    curProductState: ProductSectionProps['curProductsRef']['current'];
    quoteConfigList: List<QuoteConfig>;
    project: Project | undefined;
    formikMainProductsByPlants: QuoteFormikType['mainProductsByPlants'];
    formikAdditionalProductsByPlants: QuoteFormikType['additionalProductsByPlants'];
    formikTerms: QuoteFormikType['terms'];
    formikQuoteConfig: QuoteFormikType['quoteConfig'];
    projectID?: string | null;
    quoteConfigID?: string | null;
    isMoneySwitchOn: boolean;
    marginConfiguration?: Enums.MarginConfiguration;
    defaultMargins?: TenantDefaultMargins;
    defaultToSuggestedPrice?: boolean;
    fixedPricingByCompany?: FixedPricingByCompany;
  }) => {
    newProductState: ProductSectionProps['curProductsRef']['current'];
    newFormikMainProductsByPlants: QuoteFormikType['mainProductsByPlants'];
    newFormikAdditionalProductsByPlants: QuoteFormikType['additionalProductsByPlants'];
    newFormikTerms: QuoteFormikType['terms'];
    newFormikQuoteConfig: QuoteFormikType['quoteConfig'];
  };
};

export function useQuoteDrawer(): UseQuoteDrawerReturn {
  const contactLookups = (companyID: string | null, contactList: List<Contact>): InputOption[] =>
    lookups({
      items: contactList.items.filter((c) => c.company.id === companyID),
      label: (c) => c.fullName(),
      // I think this needs a refactoring, so for now just returning matching length.
      count: contactList.items.filter((c) => c.company.id === companyID).length,
    });

  // TODO: #1138 stop MUI warnings
  const onCompanyChange = (
    companyID: string | null,
    contactList: List<Contact>,
  ): YupSchemaNullableReferenceType[] => {
    if (!hasValue(companyID) || companyID === '') {
      return [EmptyFormikQuote.contacts[0]];
    }
    const contactOpts = contactLookups(companyID, contactList);
    return contactOpts.map((opt) => ({
      id: opt.value ?? null,
      option: opt,
    }));
  };

  const onProjectChange = (
    project: Project | undefined,
  ): {
    company: QuoteFormikType['company'];
    contacts: QuoteFormikType['contacts'];
  } => {
    if (project === undefined) {
      return {
        company: EmptyFormikQuote.company,
        contacts: EmptyFormikQuote.contacts,
      };
    }

    // If the selected project only has one company, use their company & contact IDs.
    const [onlyCompany] = project.companies;
    if (project.companies.length === 1) {
      return {
        company: {
          id: onlyCompany.company.id,
          option: {
            value: onlyCompany.company.id,
            label: onlyCompany.company.name,
          },
        },
        contacts:
          onlyCompany.contact === null
            ? EmptyFormikQuote.contacts
            : [
                {
                  id: onlyCompany.contact.id,
                  option: {
                    value: onlyCompany.contact.id,
                    label: onlyCompany.contact.fullName(),
                  },
                  isNewContact: false,
                },
              ],
      };
    }
    return {
      company: EmptyFormikQuote.company,
      contacts: EmptyFormikQuote.contacts,
    };
  };

  type HandleProductStateOpts = {
    curProductState: ProductSectionProps['curProductsRef']['current'];
    quoteConfigList: List<QuoteConfig>;
    project: Project | undefined;
    formikMainProductsByPlants: QuoteFormikType['mainProductsByPlants'];
    formikAdditionalProductsByPlants: QuoteFormikType['additionalProductsByPlants'];
    formikTerms: QuoteFormikType['terms'];
    formikQuoteConfig: QuoteFormikType['quoteConfig'];

    projectID?: string | null;
    quoteConfigID?: string | null;
    isMoneySwitchOn: boolean;
    marginConfiguration?: Enums.MarginConfiguration;
    defaultMargins?: TenantDefaultMargins;
    defaultToSuggestedPrice?: boolean;
    fixedPricingByCompany?: FixedPricingByCompany;
  };

  type HandleProductStateReturn = {
    newProductState: ProductSectionProps['curProductsRef']['current'];
    newFormikMainProductsByPlants: QuoteFormikType['mainProductsByPlants'];
    newFormikAdditionalProductsByPlants: QuoteFormikType['additionalProductsByPlants'];
    newFormikTerms: QuoteFormikType['terms'];
    newFormikQuoteConfig: QuoteFormikType['quoteConfig'];
  };

  /**
   * The QuoteProduct state can be adjusted from changing project, quoteConfig, and
   * manually adding/removing values. This helper function takes inputs and returns appropriate
   * values to set based on the outside factors of project/quote config.
   */
  const handleProductState = ({
    curProductState,
    formikMainProductsByPlants,
    formikAdditionalProductsByPlants,
    formikTerms,
    formikQuoteConfig,
    quoteConfigList,
    project,

    projectID,
    quoteConfigID,
    isMoneySwitchOn,
    marginConfiguration,
    defaultMargins,
    defaultToSuggestedPrice = false,
    fixedPricingByCompany,
  }: HandleProductStateOpts): HandleProductStateReturn => {
    // No project is selected, or a project was just cleared, so always clear state and formik products.
    if (project === undefined || projectID === null) {
      return {
        newProductState: {},
        newFormikMainProductsByPlants: [],
        newFormikAdditionalProductsByPlants: [],
        newFormikTerms: formikTerms,
        newFormikQuoteConfig: EmptyFormikQuote.quoteConfig,
      };
    }

    // A new project was selected
    if (projectID !== undefined) {
      const newFormikMainProductsByPlants = projectProductsToProductsByPlants(
        project,
        project.products, // TODO: investigate why in some cases it's empty and sometimes it's not
        isMoneySwitchOn,
        marginConfiguration,
        defaultMargins,
        fixedPricingByCompany,
      );

      const newProductState: ProductSectionProps['curProductsRef']['current'] = {};

      // Build product state from project products
      project.products.forEach((pp) => {
        newProductState[pp.product.id] = pp.product;
      });

      // If there is only one matching quote config, use that to populate additional products
      const quoteConfigSharingPlants = quoteConfigList.items.filter((qc) =>
        project.plants.some((pp) => qc.plants.some((qcPlant) => qcPlant.id === pp.plant.id)),
      );

      if (quoteConfigSharingPlants.length === 1) {
        const newQuoteConfig = quoteConfigSharingPlants[0];
        const newFormikAdditionalProductsByPlants = applyTierPricingToProductsByPlants(
          quoteConfigProductsToProductsByPlants(
            newQuoteConfig,
            isMoneySwitchOn,
            defaultMargins ?? EmptyTenantDefaultMargins,
            marginConfiguration,
          ),
          fixedPricingByCompany,
        );

        // Add additional products from quote config
        newQuoteConfig.products.forEach((qcp) => {
          newProductState[qcp.product.id] = qcp.product;
        });

        return {
          newProductState,
          newFormikMainProductsByPlants,
          newFormikAdditionalProductsByPlants,
          newFormikTerms: newQuoteConfig.terms,
          newFormikQuoteConfig: {
            id: newQuoteConfig.id,
            option: {
              label: newQuoteConfig.name,
              value: newQuoteConfig.id,
            },
          },
        };
      }

      // The new project does not have a single matching quote config, so return current values.
      return {
        newProductState,
        newFormikMainProductsByPlants,
        newFormikAdditionalProductsByPlants: [],
        newFormikTerms: formikTerms,
        newFormikQuoteConfig: formikQuoteConfig,
      };
    }

    // Quote config was cleared, so clear all additional products
    if (quoteConfigID === null) {
      return {
        newProductState: { ...curProductState },
        newFormikMainProductsByPlants: formikMainProductsByPlants,
        newFormikAdditionalProductsByPlants: [],
        newFormikTerms: formikTerms,
        newFormikQuoteConfig: EmptyFormikQuote.quoteConfig,
      };
    }

    // A new quote config was selected
    if (quoteConfigID !== undefined) {
      const newProductState = { ...curProductState };

      // Then append the new additional products from the quote config
      const newQuoteConfig =
        quoteConfigList.items.find((qc) => qc.id === quoteConfigID) ?? QuoteConfig.zero();
      const newFormikAdditionalProductsByPlants = applyTierPricingToProductsByPlants(
        quoteConfigProductsToProductsByPlants(
          newQuoteConfig,
          isMoneySwitchOn,
          defaultMargins ?? EmptyTenantDefaultMargins,
          marginConfiguration,
        ),
        fixedPricingByCompany,
      );

      newQuoteConfig.products.forEach((qcp) => {
        newProductState[qcp.product.id] = qcp.product;
      });

      return {
        newProductState,
        newFormikMainProductsByPlants: formikMainProductsByPlants,
        newFormikAdditionalProductsByPlants,
        newFormikTerms: newQuoteConfig.terms,
        newFormikQuoteConfig: {
          id: newQuoteConfig.id,
          option: {
            label: newQuoteConfig.name,
            value: newQuoteConfig.id,
          },
        },
      };
    }

    return {
      newProductState: curProductState,
      newFormikMainProductsByPlants: formikMainProductsByPlants,
      newFormikAdditionalProductsByPlants: formikAdditionalProductsByPlants,
      newFormikTerms: formikTerms,
      newFormikQuoteConfig: formikQuoteConfig,
    };
  };

  return { onCompanyChange, onProjectChange, handleProductState };
}
